import { msg } from "@omegagrid/localize";
import { MenuItem } from "@omegagrid/core";
import { NodeModel, NodeSource, TreeSource } from "@omegagrid/tree";
import { Query } from "@reporting/rp-types/ext";
import { formatQuery, getFileExtensionInfo } from "../utils";
import rp, { BrowserAdapter, BrowserOptions, helpers } from "@reporting/rp-app-host";

export class QueryBrowserAdapter extends BrowserAdapter {

	constructor(readonly idProject: number) { super() }

	async getTree(): Promise<TreeSource> {
		const treeItems: NodeSource[] = [];
		const queries = this.idProject ? await rp.ms('extensions').get<Query[]>(`queries`, {
			fields: ['id_thread', 'description'],
			where: {id_custom_page: this.idProject},
			order: [['description', 'asc']]
		}) : [];

		queries.sort((a, b) => helpers.comparePaths(a.description, b.description));

		let parents: string[] = [];
		queries.forEach(query => {
			const chunks = query.description.split('/');
			const filename = chunks.pop();

			for (let i = helpers.commonPathLength(chunks, parents); i < chunks.length; i++) {
				const dir = chunks[i];

				treeItems.push({
					k: dir,
					v: dir,
					l: i,
					collapsed: true,
					folder: BrowserAdapter.folderOptions
				});
			}

			treeItems.push({
				k: `extensions-query-${query.id_thread}`,
				v: filename,
				l: chunks.length,
				icon: getFileExtensionInfo(query.description).icon,
				props: {query: query}
			});

			parents = chunks;
		});

		if (treeItems.length == 0) {
			treeItems.push({
				k: 'extensions-queryForm',
				v: `<${msg(`New query`)}>`,
				icon: 'plus'
			});
		}

		return treeItems;
	}

	getMenuItems(node: NodeModel): MenuItem[] {
		const file = node.properties?.query as File;
		if (!file) return [];
		const items: MenuItem[] = [];
		items.push({key: 'open', value: msg('Open'), icon: 'book-open'});
		items.push({key: 'properties', value: msg('Properties'), icon: 'pen-to-square'});
		items.push({key: 'duplicate', value: msg('Duplicate'), icon: 'copy'});
		items.push({key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000'});
		return items;
	}

	async onAction(key: string, node: NodeModel) {
		const query = node.properties.query as Query;
		switch (key) {
			case 'open':
				rp.host.open(node.key, {title: node.value});
				return;
			case 'properties':
				rp.host.openModalSidebar({
					id: `extensions-queryForm-${query.id_thread}`,
					caption: query.description
				});
				return;
			case 'duplicate':
				rp.host.openModalSidebar({
					id: `extensions-queryForm-${query.id_thread}-copy`,
					caption: `${msg('Copy')}: ${query.description}`
				});
				break;
			case 'delete':
				if (!query) return;
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: `Are you sure you want to delete ${formatQuery(query)}?`
				});
				if (command?.id == 'Y') {
					await rp.ms('extensions').delete(`queries/${query.id_thread}`);
					this.browser.deleteNode(node);
				}
				return;
		}
	}

	getOptions(): Partial<BrowserOptions> {
		return this.createOptions({
			selectMode: 'none',
			expanders: false,
			contextMenuButton: true,
		});
	}
}
